import type { PlaceType } from "~/lib/api";

export interface PricingTypeDefinition {
  type: string;
  label: string;
}

const CATALOG: Partial<Record<PlaceType, PricingTypeDefinition[]>> = {
  accommodation: [
    { type: "nightly", label: "Nightly" },
    { type: "weekly", label: "Weekly" },
    { type: "monthly", label: "Monthly" },
  ],
  "ski-resort": [
    { type: "hourly", label: "Hourly" },
    { type: "half-day", label: "Half-day" },
    { type: "full-day", label: "Full-day" },
    { type: "evening", label: "Night" },
    { type: "season", label: "Season" },
  ],
  "bike-park": [
    { type: "half-day", label: "Half-day" },
    { type: "full-day", label: "Full-day" },
    { type: "season", label: "Season" },
  ],
  "hot-spring": [{ type: "admission", label: "Admission" }],
  spa: [
    { type: "evening", label: "Evening" },
    { type: "full-day", label: "Full-day" },
    { type: "season", label: "Season" },
  ],
  gym: [
    { type: "visit", label: "Per visit" },
    { type: "monthly", label: "Monthly" },
  ],
  "yoga-center": [
    { type: "class", label: "Per class" },
    { type: "monthly", label: "Monthly" },
  ],
  massage: [{ type: "session", label: "Per session" }],
  "coworking-space": [
    { type: "hourly", label: "Hourly" },
    { type: "daily", label: "Daily" },
    { type: "weekly", label: "Weekly" },
    { type: "monthly", label: "Monthly" },
  ],
  campground: [
    { type: "nightly", label: "Nightly" },
    { type: "weekly", label: "Weekly" },
    { type: "monthly", label: "Monthly" },
  ],
  hut: [{ type: "nightly", label: "Nightly" }],
  "swimming-pool": [{ type: "admission", label: "Admission" }],
  activity: [{ type: "admission", label: "Admission" }],
  parking: [
    { type: "hourly", label: "Hourly" },
    { type: "night", label: "Night" },
    { type: "24h", label: "24h" },
  ],
  "public-transport": [
    { type: "one-way", label: "One-way" },
    { type: "return", label: "Return" },
  ],
};

const FREE_APPLICABLE_PLACE_TYPES: ReadonlySet<PlaceType> = new Set([
  "hiking",
  "mtb-trail",
  "backcountry",
  "bike-park",
  "ski-resort",
  "hut",
  "campground",
  "hot-spring",
  "swimming-pool",
  "library",
  "public-park",
  "public-transport",
  "parking",
  "activity",
]);

export function getPricingTypes(placeType: PlaceType): PricingTypeDefinition[] | null {
  return CATALOG[placeType] ?? null;
}

export function supportsFreePricing(placeType: PlaceType): boolean {
  return FREE_APPLICABLE_PLACE_TYPES.has(placeType);
}

export function getPricingTypeLabel(type: string): string {
  for (const types of Object.values(CATALOG)) {
    const found = types.find((t) => t.type === type);
    if (found) return found.label;
  }
  return type;
}

export function formatPricePointLabel(price: { type: string; label?: string }): string {
  const typeName = getPricingTypeLabel(price.type);
  return price.label ? `${typeName} (${price.label})` : typeName;
}
